import { PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useKeycloak } from "@react-keycloak/web";
import { useHistory } from "react-router-dom";
import { Button, ButtonBar } from "@k8slens/lds";
import Layout from "src/components/Layout/Layout";
import { useGetLicense } from "src/hooks/useGetLicense";
import { ProfileContext } from "src/providers/profile-provider";
import { TokenContext } from "src/providers/token-provider";
import { useQuery } from "src/hooks/useQuery";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";
import { openUrl } from "src/utils/url";
import { useGetBillingInformation } from "src/hooks/useGetBillingInfo";
import { useLogout } from "src/hooks/useLogout";
import { useSubscriptionBillingUrls } from "src/hooks/useSubscriptionBillingUrls";
import { NoSubscriptions } from "src/components/pages/Home/NoSubscription";
import { ProSubscription } from "src/components/pages/Home/ProSubscription";
import { PersonalSubscription } from "src/components/pages/Home/PersonalSubscription";
import { License, LicenseContainer, LicenseType } from "src/components/pages/Home/license";
import { useGetSubscriptionSeats } from "src/hooks/useGetSubscriptionSeats";
import { useDownloadActivationCode } from "src/hooks/useDownloadActivationCode";
import { DownloadOfflineActivationCodeModal } from "src/components/DownloadOfflineActivationModal/DownloadOfflineActivationCodeModal";
import { useDownloadPersonalSubscriptionOfflineActivationCode } from "src/hooks/useDownloadPersonalSubscriptionOfflineActivationCode";

import styles from "src/components/pages/Home/Home.module.css";
import { useGetUserSSO } from "src/hooks/useGetUserSSO";
import { UserSSOBanner } from "src/components/UserSSOBanner/UserSSOBanner";
import { OFFLINE_ACTIVATION } from "src/constants";

const Home = ({ children }: PropsWithChildren<{}>) => {
  const history = useHistory();
  const { updateToken, token } = useContext(TokenContext);
  const { profile } = useContext(ProfileContext);
  const keycloak = useKeycloak();
  const query = useQuery();
  const [downloaded, setDownloaded] = useState(false);
  const platformClient = useLensPlatformClient();
  const { billingInfo } = useGetBillingInformation();
  const { subscriptionSeats, currentSubscriptionSeatWithBillingPeriod } = useGetSubscriptionSeats(profile?.username);
  const currentLicense = useGetLicense();
  const subscriptionId = currentSubscriptionSeatWithBillingPeriod?.subscriptionId;
  const { billingInfoUrl, accountUrl } = useSubscriptionBillingUrls(profile?.username, subscriptionId);
  const downloadOfflineActivationCode = useDownloadActivationCode();
  const downloadPersonalSubscriptionOfflineActivationCode = useDownloadPersonalSubscriptionOfflineActivationCode();
  const [isModalOpened, setIsModalOpened] = useState(false);
  const { userSSOAccountsConnectionLinks } = useGetUserSSO();

  const handleLicenseChange = () => {
    history.push("/subscribe");
  };

  useEffect(() => {
    if (
      query.get("client_id") &&
      query.get("action") === "download" &&
      !downloaded &&
      keycloak?.keycloak?.clientId === "lens-extension" &&
      currentSubscriptionSeatWithBillingPeriod
    ) {
      downloadOfflineActivationCode(profile?.username as string, currentSubscriptionSeatWithBillingPeriod.id!);
      setDownloaded(true);

      return;
    }

    if (
      query.get("client_id") &&
      query.get("action") === "download" &&
      !downloaded &&
      keycloak?.keycloak?.clientId === "lens-extension" &&
      currentLicense?.type === LicenseType.personal
    ) {
      downloadPersonalSubscriptionOfflineActivationCode();
      setDownloaded(true);
    }
  }, [
    query,
    downloaded,
    keycloak.keycloak.clientId,
    currentSubscriptionSeatWithBillingPeriod,
    downloadPersonalSubscriptionOfflineActivationCode,
    currentLicense?.type,
    downloadOfflineActivationCode,
    profile?.username,
  ]);

  const handleDownload = () => {
    if (query.get("client_id") && query.get("action") === "download") {
      setDownloaded(false);
      setIsModalOpened(false);
    } else {
      // eslint-disable-next-line xss/no-location-href-assign
      window.location.href = `${window.location.origin}${window.location.pathname}?client_id=lens-extension&action=download`;
    }
  };

  const handleCancelSubscription = async (license: License) => {
    if (license.type === LicenseType.personal && profile && profile.username) {
      await platformClient.user.deactivateSubscription({
        username: profile?.username,
        license: { subscriptionId: "personal" },
      });
      updateToken(-1);
    } else if (accountUrl && (license.type === LicenseType.pro || license.type === LicenseType.proTrial)) {
      openUrl(accountUrl);
    }
  };

  const LicenseContainer: LicenseContainer = useMemo(() => {
    if (!currentLicense) {
      return NoSubscriptions;
    }

    switch (currentLicense.type) {
      case LicenseType.personal:
        return PersonalSubscription;
      default:
        return ProSubscription;
    }
  }, [currentLicense]);

  const logout = useLogout();

  const canDownloadOfflineActivationCode = useMemo(
    () => Boolean(currentLicense?.features?.includes(OFFLINE_ACTIVATION)),
    [currentLicense?.features],
  );

  const isPersonalLicense = useMemo(() => token?.license_type === "personal", [token?.license_type]);

  return (
    <Layout className="bg-secondary" logout={logout} profile={profile}>
      <div className={styles.home}>
        <div>
          {profile && (
            <>
              <UserSSOBanner userSSOLinks={userSSOAccountsConnectionLinks} />
              <section className="mb-6">
                <h2 className={clsx("lds-h2", styles.welcome)}>Manage your Lens ID</h2>
                <p>Lens ID is your personal identity in the Lens universe.</p>

                <h3 className={styles.title}>Your Active Lens Subscription</h3>
                <p>
                  Some products and services (such as Lens Desktop and Lens Cloud Services) made available to you may
                  require active Lens Subscription. Your currently active subscription is displayed below.
                </p>
              </section>
              <section className={styles.subscriptions}>
                <LicenseContainer
                  onLicenseChange={handleLicenseChange}
                  onCancelSubscription={handleCancelSubscription}
                  currentSubscriptionSeatWithBillingPeriod={currentSubscriptionSeatWithBillingPeriod}
                  billingInfoUrl={billingInfoUrl}
                  billingInfo={billingInfo}
                  accountUrl={accountUrl}
                  subscriptionSeats={subscriptionSeats}
                  license={currentLicense}
                />
              </section>
              {token?.license_type ? (
                <section className="mt-6">
                  <h3 className={styles.title}>Lens ID offline activation code</h3>
                  <p>
                    Activate Lens Desktop with Activation Code. This activation method works for offline and air-gapped
                    environments. This activation code is for your use and should not be shared.
                  </p>
                  <ButtonBar className={styles.offlineButtonBar}>
                    <Button
                      label="Download activation code"
                      disabled={!canDownloadOfflineActivationCode}
                      primary
                      onClick={() => setIsModalOpened(true)}
                    />
                  </ButtonBar>
                  {!canDownloadOfflineActivationCode && !isPersonalLicense && (
                    <p>
                      <i>
                        <small>Your administrator has disabled the use of offline activation.</small>
                      </i>
                    </p>
                  )}
                  {!canDownloadOfflineActivationCode && isPersonalLicense && (
                    <p>
                      <small>
                        Offline activation is available with a Lens Pro subscription. Upgrade{" "}
                        <a className="primary" href="subscribe">
                          here.
                        </a>
                      </small>
                    </p>
                  )}
                </section>
              ) : null}
            </>
          )}
        </div>
      </div>
      {children}

      {canDownloadOfflineActivationCode && (
        <DownloadOfflineActivationCodeModal
          isOpened={isModalOpened}
          onDownload={handleDownload}
          onCancel={() => setIsModalOpened(false)}
        />
      )}
    </Layout>
  );
};

export default Home;
